import type { VoucherAvailableCountsRequest, VoucherResponse } from '@/api/model';
import { ProviderCategory } from '@/api/model/providerCategory';
import { VoucherStatus } from '@/api/model/voucherStatus';
import type { VoucherSortOrder as ApiVoucherSortOrder } from '@/api/model/voucherSortOrder';
import { VoucherSortOrder as ApiVoucherSortOrderConst } from '@/api/model/voucherSortOrder';
import { useGetVoucherAvailableCountsBulk } from '@/api/voucher-codes/voucher-codes';
import { useListVouchers, useExportMonthlyRedemptions, useArchiveVoucher } from '@/api/vouchers/vouchers';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CardDescription, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, ArrowUpDown, Upload, Pencil, Trash2 } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { Input } from '@/components/ui/input';
import { parseISOToDate } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/api-error';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { VoucherPagination } from '@/components/vouchers/VoucherPagination';

const ITEMS_PER_PAGE = 10;

export const VoucherManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const countsMutation = useGetVoucherAvailableCountsBulk();
  // Local UI state for admin toolbar
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('alle');
  const [selectedStatus, setSelectedStatus] = useState<string>('alle');
  const [selectedSort, setSelectedSort] = useState<string>('neueste');
  const [currentPage, setCurrentPage] = useState<number>(1);
  // Export month state
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    const m = `${now.getMonth() + 1}`.padStart(2, '0');
    return `${now.getFullYear()}-${m}`; // yyyy-MM
  });
  const hasMonth = Boolean(selectedMonth);

  const serverSortOrder: ApiVoucherSortOrder =
    selectedSort === 'aelteste' ? ApiVoucherSortOrderConst.oldest : ApiVoucherSortOrderConst.newest;

  const voucherQueryParams = React.useMemo(
    () => ({
      is_active: true, // Only show active vouchers
      sort_order: serverSortOrder,
    }),
    [serverSortOrder]
  );

  const { data: vouchersData, isLoading, isError, error } = useListVouchers(voucherQueryParams);
  const archiveMutation = useArchiveVoucher();

  const vouchers: VoucherResponse[] = React.useMemo(
    () => (vouchersData?.items || []) as VoucherResponse[],
    [vouchersData?.items]
  );

  const voucherIdsKey = React.useMemo(() => vouchers.map(v => v.id).join(','), [vouchers]);

  React.useEffect(() => {
    const ids = vouchers.map(v => v.id);
    if (ids.length === 0) return;
    const body: VoucherAvailableCountsRequest = { voucher_ids: ids };
    countsMutation.mutate({ data: body });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherIdsKey]);

  // Filter and sort vouchers based on UI state
  const filteredAndSortedVouchers = React.useMemo(() => {
    let filtered = [...vouchers];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        voucher => voucher.title.toLowerCase().includes(query) || voucher.provider.name.toLowerCase().includes(query)
      );
    }

    // Apply category filter
    if (selectedCategory !== 'alle') {
      const categoryMap: Record<string, ProviderCategory> = {
        gastronomie: ProviderCategory.GASTRONOMIE,
        erleben: ProviderCategory.ERLEBEN,
        shopping: ProviderCategory.SHOPPING,
        services: ProviderCategory.SERVICES,
      };
      const category = categoryMap[selectedCategory];
      if (category) {
        filtered = filtered.filter(voucher => voucher.provider.category === category);
      }
    }

    // Apply status filter
    if (selectedStatus !== 'alle') {
      filtered = filtered.filter(voucher => voucher.status === selectedStatus);
    }

    const getCreatedAtTimestamp = (voucher: VoucherResponse): number | null => {
      if (!voucher.created_at) {
        return null;
      }

      try {
        return parseISOToDate(voucher.created_at).getTime();
      } catch (_error) {
        return null;
      }
    };

    const sorted = [...filtered];
    switch (selectedSort) {
      case 'neueste':
        sorted.sort((a, b) => {
          const dateA = getCreatedAtTimestamp(a);
          const dateB = getCreatedAtTimestamp(b);

          if (dateA === null && dateB === null) return 0;
          if (dateA === null) return 1;
          if (dateB === null) return -1;

          return dateB - dateA;
        });
        break;
      case 'aelteste':
        sorted.sort((a, b) => {
          const dateA = getCreatedAtTimestamp(a);
          const dateB = getCreatedAtTimestamp(b);

          if (dateA === null && dateB === null) return 0;
          if (dateA === null) return -1;
          if (dateB === null) return 1;

          return dateA - dateB;
        });
        break;
      case 'titel-az':
        sorted.sort((a, b) => a.title.localeCompare(b.title, 'de'));
        break;
      case 'titel-za':
        sorted.sort((a, b) => b.title.localeCompare(a.title, 'de'));
        break;
      default:
        break;
    }

    return sorted;
  }, [vouchers, searchQuery, selectedCategory, selectedStatus, selectedSort]);
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedStatus, selectedSort]);

  const totalPages = React.useMemo(() => {
    if (filteredAndSortedVouchers.length === 0) {
      return 1;
    }
    return Math.ceil(filteredAndSortedVouchers.length / ITEMS_PER_PAGE);
  }, [filteredAndSortedVouchers.length]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedVouchers = React.useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedVouchers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredAndSortedVouchers, currentPage]);

  const [computedYearStr, computedMonthStr] = (selectedMonth || '1970-01').split('-');
  const computedYear = Number(computedYearStr);
  const computedMonth = Number(computedMonthStr);
  const exportQuery = useExportMonthlyRedemptions(
    { year: computedYear, month: computedMonth },
    { query: { enabled: false } }
  );
  const handleDownloadCsv = useCallback(() => {
    if (!selectedMonth) return;
    const [yearStr, monthStr] = selectedMonth.split('-');

    exportQuery
      .refetch()
      .then(result => {
        const data = result.data;
        if (!data) {
          throw new Error('Leere Antwort vom Server');
        }

        const blob = data instanceof Blob ? data : new Blob([data as ArrayBuffer], { type: 'text/csv' });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `einloesungen-${yearStr}-${monthStr}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(downloadUrl);
      })
      .catch(e => {
        const message = e instanceof Error ? e.message : 'Unbekannter Fehler';
        alert(`Fehler beim Export: ${message}`);
      });
  }, [exportQuery, selectedMonth]);

  const handleArchiveVoucher = useCallback(
    (voucherId: string) => {
      archiveMutation.mutate(
        { voucherId },
        {
          onSuccess: () => {
            toast.success('Gutschein wurde archiviert');
            queryClient.invalidateQueries({ queryKey: ['listVouchers'] });
            countsMutation.mutate({
              data: { voucher_ids: vouchers.map(v => v.id) },
            });
          },
          onError: error => {
            toast.error(getApiErrorMessage(error, 'Fehler beim Archivieren des Gutscheins'));
          },
        }
      );
    },
    [archiveMutation, queryClient, countsMutation, vouchers]
  );

  if (isLoading) {
    return (
      <div className='bg-white rounded-xl border p-6'>
        <p className='text-center text-muted-foreground'>Gutscheine werden geladen...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className='bg-white rounded-xl border p-6'>
        <p className='text-center text-red-500'>
          Fehler beim Laden der Gutscheine: {error instanceof Error ? error.message : 'Unbekannter Fehler'}
        </p>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case VoucherStatus.PUBLISHED:
        return 'bg-swd-gruen-light text-swd-tuerkis-dark border-0';
      case VoucherStatus.DRAFT:
        return 'bg-white text-swd-tuerkis-darker border border-swd-tuerkis-darker border-dashed';
      case VoucherStatus.ARCHIVED:
        return 'bg-neutral-hellgrau text-neutral-dunkelgrau border-0';
      default:
        return 'bg-muted text-neutral-dunkelgrau';
    }
  };

  const getCategoryDisplayName = (category: ProviderCategory): string => {
    switch (category) {
      case ProviderCategory.GASTRONOMIE:
        return 'Gastronomie';
      case ProviderCategory.ERLEBEN:
        return 'Erleben';
      case ProviderCategory.SHOPPING:
        return 'Shopping';
      case ProviderCategory.SERVICES:
        return 'Services';
      default:
        return category;
    }
  };

  return (
    <div className='space-y-6'>
      {/* Page header (not a card) */}
      <section className='flex items-end justify-between gap-4'>
        <div className='flex flex-col gap-2'>
          <CardTitle className='text-swd-tuerkis-darker text-3xl leading-9 font-semibold'>Gutscheine</CardTitle>
          <CardDescription className='text-neutral-grau tracking-wide'>
            Verwalten, erstellen und bearbeiten Sie alle Gutscheine im System.
          </CardDescription>
        </div>
        <div className='flex items-end gap-3'>
          <Button asChild size='sm' variant='enabled'>
            <Link to='/admin/vouchers/create'>Neuen Gutschein erstellen</Link>
          </Button>
        </div>
      </section>

      {/* Toolbar below header - filters and export */}
      <section className='flex items-center justify-between gap-3 flex-wrap rounded-xl border border-neutral-hellgrau bg-white px-3 py-4'>
        <div className='flex items-center gap-3 flex-wrap'>
          {/* Search */}
          <div className='flex items-center gap-2'>
            <div className='relative'>
              <Input
                placeholder='Suche nach Name oder Partner...'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Filter is applied automatically via useMemo
                  }
                }}
                className='pr-8'
              />
              <Search className='absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none' />
            </div>
          </div>

          {/* Kategorie */}
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger size='sm'>
              <SelectValue placeholder='Alle Kategorien' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='alle'>Alle Kategorien</SelectItem>
              <SelectItem value='gastronomie'>Gastronomie</SelectItem>
              <SelectItem value='erleben'>Erleben</SelectItem>
              <SelectItem value='shopping'>Shopping</SelectItem>
              <SelectItem value='services'>Services</SelectItem>
            </SelectContent>
          </Select>

          {/* Status */}
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger size='sm'>
              <SelectValue placeholder='Status Alle' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='alle'>Status Alle</SelectItem>
              <SelectItem value={VoucherStatus.PUBLISHED}>Veröffentlicht</SelectItem>
              <SelectItem value={VoucherStatus.DRAFT}>Entwurf</SelectItem>
              <SelectItem value={VoucherStatus.ARCHIVED}>Archiviert</SelectItem>
            </SelectContent>
          </Select>

          {/* Sortieren */}
          <Select value={selectedSort} onValueChange={setSelectedSort}>
            <SelectTrigger size='sm' className='whitespace-nowrap'>
              <ArrowUpDown className='mr-2 h-4 w-4' />
              <SelectValue placeholder='Sortieren nach Neueste' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='neueste'>Neueste</SelectItem>
              <SelectItem value='aelteste'>Älteste</SelectItem>
              <SelectItem value='titel-az'>Titel A–Z</SelectItem>
              <SelectItem value='titel-za'>Titel Z–A</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Export */}
        <div className='flex items-center gap-3'>
          <Input
            type='month'
            aria-label='Export-Monat wählen'
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className='w-40'
          />
          <Button
            variant='outline'
            size='sm'
            rounded
            className='text-neutral-dunkelgrau border-neutral-hellgrau rounded-sm font-normal'
            onClick={handleDownloadCsv}
            disabled={!hasMonth}
            loading={exportQuery.isFetching}
          >
            <Upload className='mr-2 h-4 w-4' /> Exportieren
          </Button>
        </div>
      </section>

      {/* Content area */}
      {filteredAndSortedVouchers.length === 0 ? (
        <div className='text-center py-8 bg-white rounded-xl border border-neutral-hellgrau'>
          <p className='text-muted-foreground mb-4'>
            {vouchers.length === 0 ? 'Keine Gutscheine gefunden' : 'Keine Gutscheine entsprechen den Filtern'}
          </p>
        </div>
      ) : (
        <div className='rounded-xl border border-neutral-hellgrau overflow-hidden bg-white'>
          <Table className='min-w-[960px]'>
            <TableHeader className='bg-swd-hellblau'>
              <TableRow>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Titel
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Partner
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Kategorie
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-right w-[60px]'>
                  Wert
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-right w-[100px]'>
                  Einlösungen
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Status
                </TableHead>

                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap w-[100px] text-center'>
                  Aktionen
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedVouchers.map((voucher: VoucherResponse) => (
                <TableRow key={voucher.id} className='hover:bg-swd-hellblau/40 border-b border-neutral-hellgrau'>
                  <TableCell className='font-medium text-neutral-dunkelgrau align-middle'>{voucher.title}</TableCell>
                  <TableCell className='align-middle font-medium text-neutral-grau'>{voucher.provider.name}</TableCell>
                  <TableCell className='align-middle font-medium text-neutral-grau'>
                    {getCategoryDisplayName(voucher.provider.category)}
                  </TableCell>
                  <TableCell className='text-right align-middle font-medium text-neutral-grau'>
                    {`€${voucher.value}`}
                  </TableCell>
                  <TableCell className='text-right align-middle font-medium text-neutral-grau'>
                    {voucher.current_redemptions}
                    {voucher.max_redemptions && ` / ${voucher.max_redemptions}`}
                  </TableCell>
                  <TableCell className='align-middle'>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <Badge className={`rounded-2xl px-2 py-0.5 font-bold ${getStatusColor(voucher.status)}`}>
                        {voucher.status}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className='text-center align-middle'>
                    <div className='flex items-center justify-center gap-2'>
                      <Button variant='ghost' size='sm' asChild className='h-8 w-8 p-0'>
                        <Link to={`/admin/vouchers/${voucher.id}`}>
                          <Pencil className='h-4 w-4 text-swd-tuerkis-darker' />
                        </Link>
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                            <Trash2 className='h-4 w-4 text-swd-tuerkis-darker' />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Gutschein archivieren?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Möchten Sie den Gutschein "{voucher.title}" wirklich archivieren? Der Gutschein wird nicht
                              mehr in der Liste angezeigt, kann aber später wieder aktiviert werden.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleArchiveVoucher(voucher.id)}
                              disabled={archiveMutation.isPending}
                            >
                              {archiveMutation.isPending ? 'Wird archiviert...' : 'Archivieren'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className='border-t border-neutral-hellgrau bg-white px-6 py-4'>
              <VoucherPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalVouchers={filteredAndSortedVouchers.length}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
